import {
  AnalyticsComponentProvider,
  AnalyticsScreenProvider,
  AppShell,
  Card,
  Flex,
  KpiCard,
  PageGridCell,
  PageGridRow,
  StandardLayout,
  SwipeablePanel,
  Text,
} from "@wayflyer/flyui";
import {
  ChartFilterSwitches,
  ColumnChart,
  DatePickerHeader,
  TemporalResolutionPickers,
  TimeSeriesChart,
  type ChartFilterSwitchesProps,
  type DatePickerHeaderProps,
  type ReferencedMultiChartDataPoint,
  type TemporalResolutionPickersProps,
  type TimeSeriesChartLineColor,
  type ValueType,
} from "@wayflyer/flyui-dataviz";

import type { RevenueSummaryItem } from "../revenue-datapoints";

import { getRevenueComposition, revenueLinePoints } from "./internal-revenue-chart-data";

export interface RevenueScreenProps {
  pageTitle: string;
  chartData: ReferencedMultiChartDataPoint[];
  datePickerHeaderProps: DatePickerHeaderProps;
  hint?: string;
  groups: string[];
  groupMapping: Record<string, number>;
  formatValue: (rawValue: ValueType, isRef?: boolean) => string;
  chartFilters: Omit<ChartFilterSwitchesProps, "switchOneName" | "switchTwoName"> &
    Partial<Pick<ChartFilterSwitchesProps, "switchOneName" | "switchTwoName">> &
    TemporalResolutionPickersProps;
  thisYearEnabled: boolean;
  lastYearEnabled: boolean;
  summaryData: RevenueSummaryItem[];
  onSelectSummaryTab: (group: string, selected: boolean) => void;
  percentageChartData: ReferencedMultiChartDataPoint[];
  formatPercentageValue: (rawValue: ValueType, isRef?: boolean) => string;
}

const fullWidth = { xxs: { span: 6 }, sm: { span: 8 }, md: { span: 12 } };
const lineColors: TimeSeriesChartLineColor[] = [
  "var(--palette-dataviz-categorical-category4)",
  "var(--palette-dataviz-sequential-caelum-900)",
  "var(--palette-dataviz-sequential-caelum-800)",
  "var(--palette-dataviz-sequential-viridis-600)",
  "var(--palette-dataviz-sequential-viridis-900)",
  "var(--palette-dataviz-categorical-category7)",
];
const barColors: TimeSeriesChartLineColor[] = [
  "var(--palette-dataviz-categorical-category1)",
  "var(--palette-dataviz-categorical-category2)",
  "var(--palette-dataviz-categorical-category3)",
  "var(--palette-dataviz-categorical-category4)",
  "var(--palette-dataviz-categorical-category5)",
  "var(--palette-dataviz-categorical-category6)",
  "var(--palette-dataviz-categorical-category7)",
];

export const InternalRevenueScreen = ({
  pageTitle,
  chartData,
  datePickerHeaderProps,
  hint = "",
  groups,
  groupMapping,
  formatValue,
  chartFilters,
  thisYearEnabled,
  lastYearEnabled,
  summaryData,
  onSelectSummaryTab,
  percentageChartData,
  formatPercentageValue,
}: RevenueScreenProps) => {
  const { onClickDateRes, hideMonthly, hideWeekly, selectedButtonIndex, ...switchProps } = chartFilters;
  const lines = groups.flatMap((group) => {
    const color = lineColors[(groupMapping[group] ?? 0) % lineColors.length];
    return [
      ...(thisYearEnabled
        ? [{ key: `${group}-current`, label: `${group} This Year`, color, period: "Current" as const }]
        : []),
      ...(lastYearEnabled
        ? [{ key: `${group}-reference`, label: `${group} Last Year`, color, period: "Reference" as const }]
        : []),
    ].map((line) => ({ ...line, data: revenueLinePoints(chartData, group, line.period) }));
  });
  const composition = getRevenueComposition(summaryData, groups, percentageChartData);
  const bars = composition.series.map((series) => ({ ...series, color: barColors[series.colorIndex] }));
  const snapPoints = chartData.map((row) => Date.parse(`${row.dayRaw}T00:00:00Z`));
  const categories = percentageChartData.map((row) => String(row.dayRaw));
  const dateLabels = new Map(chartData.map((row) => [Date.parse(`${row.dayRaw}T00:00:00Z`), row.day]));
  const compositionLabels = new Map(percentageChartData.map((row) => [String(row.dayRaw), row.day]));
  const formatDate = (x: number) => dateLabels.get(x) ?? "";
  const formatCategory = (category: string) => compositionLabels.get(category) ?? category;

  return (
    <AnalyticsScreenProvider screen="internal-revenue">
      <AppShell>
        <StandardLayout title={pageTitle}>
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <Flex direction="column" gap="4">
                {hint ? <Text color="subtle">{hint}</Text> : null}
                <AnalyticsComponentProvider component="shared-filters">
                  <Flex direction="column" gap="4">
                    <DatePickerHeader {...datePickerHeaderProps} summary="" useSimplePanel inlineLeft />
                    <TemporalResolutionPickers
                      onClickDateRes={onClickDateRes}
                      hideWeekly={hideWeekly}
                      hideMonthly={hideMonthly}
                      selectedButtonIndex={selectedButtonIndex}
                      className="no-padding"
                    />
                  </Flex>
                </AnalyticsComponentProvider>
              </Flex>
            </PageGridCell>
          </PageGridRow>
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <AnalyticsComponentProvider component="group-selection">
                <SwipeablePanel
                  scrollerAccessibilityLabel="Revenue by store"
                  backwardScrollButtonAccessibilityLabel="Reveal previous stores"
                  forwardScrollButtonAccessibilityLabel="Reveal more stores"
                >
                  <Flex gap="4" wrap="nowrap">
                    {summaryData.map((item) => (
                      <Flex key={item.metricType} shrink={0} minWidth="max-content">
                        <KpiCard
                          title={item.title}
                          {...(item.rawMetricValue == null && item.trendValue == null
                            ? { amount: item.metricValue ?? "" }
                            : {
                                amount: {
                                  value: item.rawMetricValue ?? null,
                                  currencyCode: item.currencyCode ?? "USD",
                                  formatOptions: {
                                    minimumFractionDigits: item.fractionDigits ?? 0,
                                    maximumFractionDigits: item.fractionDigits ?? 0,
                                    currencyDisplay: "symbol",
                                  },
                                },
                                trend:
                                  item.trendValue == null
                                    ? undefined
                                    : {
                                        value: item.trendValue,
                                        iconVariant: "straight",
                                        status: item.metricReferenceSuccess ? "positive" : "critical",
                                        formatFn: () => item.metricReferenceValue ?? "",
                                      },
                              })}
                          subtext={
                            item.metricReferenceValue
                              ? `${item.trendValue == null ? `${item.metricReferenceValue} ` : ""}vs. last year`
                              : undefined
                          }
                          infoTooltip={item.tooltip}
                          isSelected={groups.includes(item.metricType)}
                          onAction={() => onSelectSummaryTab(item.metricType, !groups.includes(item.metricType))}
                        />
                      </Flex>
                    ))}
                  </Flex>
                </SwipeablePanel>
              </AnalyticsComponentProvider>
            </PageGridCell>
          </PageGridRow>
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <AnalyticsComponentProvider component="absolute-revenue">
                <Card
                  slots={{
                    header: (
                      <Card.ChartHeader
                        title="Revenue"
                        headingElement="h2"
                        slots={{
                          controls: (
                            <ChartFilterSwitches
                              {...switchProps}
                              switchOneName={switchProps.switchOneName ?? "this-year"}
                              switchTwoName={switchProps.switchTwoName ?? "last-year"}
                              switchOneOn={thisYearEnabled}
                              switchTwoOn={lastYearEnabled}
                            />
                          ),
                        }}
                      />
                    ),
                  }}
                >
                  <TimeSeriesChart.Root
                    label="Revenue"
                    snapPoints={snapPoints}
                    xDomain={
                      snapPoints.length
                        ? [snapPoints[0], snapPoints[snapPoints.length - 1]]
                        : [Date.parse(datePickerHeaderProps.startDate), Date.parse(datePickerHeaderProps.endDate)]
                    }
                    status={chartData.length ? "ready" : "empty"}
                  >
                    <TimeSeriesChart.Plot formatX={formatDate} formatY={formatValue}>
                      {lines.map((line) => (
                        <TimeSeriesChart.Series
                          key={line.key}
                          seriesKey={line.key}
                          label={line.label}
                          data={line.data}
                          color={line.color}
                          curve="monotone"
                          before={line.period === "Reference" ? "dashed" : "solid"}
                        />
                      ))}
                      <TimeSeriesChart.Inspector>
                        {(point) => (
                          <TimeSeriesChart.Tooltip title={formatDate(point.x)}>
                            {point.entries.map((entry) => (
                              <TimeSeriesChart.TooltipRow
                                key={entry.seriesKey}
                                label={entry.label}
                                value={formatValue(entry.value, entry.seriesKey.endsWith("-reference"))}
                              />
                            ))}
                          </TimeSeriesChart.Tooltip>
                        )}
                      </TimeSeriesChart.Inspector>
                    </TimeSeriesChart.Plot>
                    <TimeSeriesChart.Legend>
                      {lines.map((line) => (
                        <TimeSeriesChart.LegendItem
                          key={line.key}
                          color={line.color}
                          indicator={line.period === "Reference" ? "dashed" : "solid"}
                        >
                          {line.label}
                        </TimeSeriesChart.LegendItem>
                      ))}
                    </TimeSeriesChart.Legend>
                  </TimeSeriesChart.Root>
                </Card>
              </AnalyticsComponentProvider>
            </PageGridCell>
          </PageGridRow>
          <PageGridRow>
            <PageGridCell layout={fullWidth}>
              <AnalyticsComponentProvider component="revenue-composition">
                <Card slots={{ header: <Card.ChartHeader title="Revenue composition" headingElement="h2" /> }}>
                  <ColumnChart.Root
                    label="Revenue composition"
                    categories={categories}
                    status={categories.length ? "ready" : "empty"}
                  >
                    <ColumnChart.Plot formatCategory={formatCategory} formatY={formatPercentageValue}>
                      {bars.map((bar) => (
                        <ColumnChart.Series
                          key={bar.key}
                          seriesKey={bar.key}
                          label={bar.label}
                          data={bar.data}
                          color={bar.color}
                          stack="stores"
                        />
                      ))}
                      <ColumnChart.Inspector>
                        {(point) => (
                          <ColumnChart.Tooltip title={formatCategory(point.category)}>
                            {(composition.tooltipRows.get(point.category) ?? []).map((entry) => (
                              <ColumnChart.TooltipRow
                                key={entry.key}
                                label={entry.label}
                                value={formatPercentageValue(entry.value)}
                              />
                            ))}
                          </ColumnChart.Tooltip>
                        )}
                      </ColumnChart.Inspector>
                    </ColumnChart.Plot>
                    <ColumnChart.Legend>
                      {bars.map((bar) => (
                        <ColumnChart.LegendItem key={bar.key} color={bar.color}>
                          {bar.label}
                        </ColumnChart.LegendItem>
                      ))}
                    </ColumnChart.Legend>
                  </ColumnChart.Root>
                </Card>
              </AnalyticsComponentProvider>
            </PageGridCell>
          </PageGridRow>
        </StandardLayout>
      </AppShell>
    </AnalyticsScreenProvider>
  );
};
